import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useRedirects, type Redirect } from '@/hooks/useRedirects';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, Pencil, Trash2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

export const Route = createFileRoute('/admin/redirects')({
  component: AdminRedirects,
});

function AdminRedirects() {
  const { redirects, isLoading, addRedirect, updateRedirect, deleteRedirect } = useRedirects();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingRedirect, setEditingRedirect] = useState<Redirect | null>(null);
  const [copiedNginx, setCopiedNginx] = useState(false);

  const [fromPath, setFromPath] = useState('');
  const [toPath, setToPath] = useState('');
  const [isPrefix, setIsPrefix] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);

  const resetForm = () => {
    setFromPath(''); setToPath(''); setIsPrefix(false); setIsActive(true); setFormError(null);
  };

  const handleOpenEdit = (r: Redirect) => {
    setEditingRedirect(r);
    setFromPath(r.from_path); setToPath(r.to_path); setIsPrefix(r.is_prefix); setIsActive(r.is_active); setFormError(null);
  };

  const handleCloseEdit = () => { setEditingRedirect(null); resetForm(); };

  const handleAdd = async () => {
    if (!fromPath || !toPath) { setFormError('Bitte füllen Sie alle Felder aus'); return; }
    const result = await addRedirect({
      from_path: fromPath.startsWith('/') ? fromPath : '/' + fromPath,
      to_path: toPath.startsWith('/') ? toPath : '/' + toPath,
      is_prefix: isPrefix,
      is_active: isActive,
    });
    if (result.success) { toast.success('Weiterleitung hinzugefügt'); setIsAddOpen(false); resetForm(); }
    else setFormError(result.error || 'Fehler beim Speichern');
  };

  const handleUpdate = async () => {
    if (!editingRedirect || !fromPath || !toPath) { setFormError('Bitte füllen Sie alle Felder aus'); return; }
    const result = await updateRedirect(editingRedirect.id, {
      from_path: fromPath.startsWith('/') ? fromPath : '/' + fromPath,
      to_path: toPath.startsWith('/') ? toPath : '/' + toPath,
      is_prefix: isPrefix,
      is_active: isActive,
    });
    if (result.success) { toast.success('Weiterleitung aktualisiert'); handleCloseEdit(); }
    else setFormError(result.error || 'Fehler beim Speichern');
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Weiterleitung wirklich löschen?')) return;
    const result = await deleteRedirect(id);
    if (result.success) toast.success('Weiterleitung gelöscht');
    else toast.error(result.error || 'Fehler beim Löschen');
  };

  const handleToggleActive = async (r: Redirect) => {
    const result = await updateRedirect(r.id, { is_active: !r.is_active });
    if (result.success) toast.success(r.is_active ? 'Deaktiviert' : 'Aktiviert');
  };

  const generateNginxConfig = () => {
    const active = redirects.filter((r) => r.is_active);
    if (active.length === 0) return '# Keine aktiven Weiterleitungen';
    const lines = ['# 301 Redirects - Generiert am ' + new Date().toLocaleDateString('de-DE')];
    for (const r of active) {
      if (r.is_prefix) {
        const esc = r.from_path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        lines.push(`rewrite ^${esc}(.*)$ ${r.to_path}$1 permanent;`);
      } else {
        lines.push(`location = ${r.from_path} { return 301 ${r.to_path}; }`);
      }
    }
    return lines.join('\n');
  };

  const copyNginxConfig = () => {
    navigator.clipboard.writeText(generateNginxConfig());
    setCopiedNginx(true);
    toast.success('Nginx-Konfiguration kopiert');
    setTimeout(() => setCopiedNginx(false), 2000);
  };

  const FormContent = () => (
    <div className="space-y-4">
      {formError && <Alert variant="destructive"><AlertDescription>{formError}</AlertDescription></Alert>}
      <div className="space-y-2">
        <Label htmlFor="fromPath">Von (alte URL)</Label>
        <Input id="fromPath" value={fromPath} onChange={(e) => setFromPath(e.target.value)} placeholder="/alte-seite" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="toPath">Nach (neue URL)</Label>
        <Input id="toPath" value={toPath} onChange={(e) => setToPath(e.target.value)} placeholder="/neue-seite" />
      </div>
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label htmlFor="isPrefix">Präfix-Matching</Label>
          <p className="text-sm text-muted-foreground">Alle URLs, die mit dem Pfad beginnen</p>
        </div>
        <Switch id="isPrefix" checked={isPrefix} onCheckedChange={setIsPrefix} />
      </div>
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label htmlFor="isActive">Aktiv</Label>
          <p className="text-sm text-muted-foreground">Weiterleitung ist aktiv</p>
        </div>
        <Switch id="isActive" checked={isActive} onCheckedChange={setIsActive} />
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Weiterleitungen</h1>
          <p className="text-muted-foreground mt-2">Verwalten Sie 301-Weiterleitungen für alte URLs.</p>
        </div>
        <Dialog open={isAddOpen} onOpenChange={(open) => { setIsAddOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button><Plus className="h-4 w-4 mr-2" />Hinzufügen</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Neue Weiterleitung</DialogTitle>
              <DialogDescription>Erstellen Sie eine neue 301-Weiterleitung.</DialogDescription>
            </DialogHeader>
            <FormContent />
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddOpen(false)}>Abbrechen</Button>
              <Button onClick={handleAdd}>Speichern</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Alle Weiterleitungen</CardTitle>
          <CardDescription>{redirects.length} Weiterleitung{redirects.length !== 1 ? 'en' : ''} konfiguriert</CardDescription>
        </CardHeader>
        <CardContent>
          {redirects.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Noch keine Weiterleitungen konfiguriert.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Von</TableHead>
                  <TableHead>Nach</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {redirects.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-sm">{r.from_path}</TableCell>
                    <TableCell className="font-mono text-sm">{r.to_path}</TableCell>
                    <TableCell><Badge variant={r.is_prefix ? 'secondary' : 'outline'}>{r.is_prefix ? 'Präfix' : 'Exakt'}</Badge></TableCell>
                    <TableCell><Switch checked={r.is_active} onCheckedChange={() => handleToggleActive(r)} /></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(r)}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(r.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editingRedirect} onOpenChange={(open) => !open && handleCloseEdit()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Weiterleitung bearbeiten</DialogTitle>
            <DialogDescription>Bearbeiten Sie die Weiterleitung.</DialogDescription>
          </DialogHeader>
          <FormContent />
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEdit}>Abbrechen</Button>
            <Button onClick={handleUpdate}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle>Nginx-Konfiguration</CardTitle>
          <CardDescription>Kopieren Sie diese Konfiguration in Ihre Nginx-Einstellungen für serverseitige Weiterleitungen.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm font-mono">{generateNginxConfig()}</pre>
            <Button variant="outline" size="sm" className="absolute top-2 right-2" onClick={copyNginxConfig}>
              {copiedNginx ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
